import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/pro-solid-svg-icons/faTrash';
import React, { useState, useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { INLINE_MESSAGE_TYPES } from 'ui-components/components/inline-message';
import { Input, version2 } from 'ui-components/components/input';

import { useEditRecipientTaxIdentifiersFormContext } from './context';
import { messages } from './messages';
import style from './styles.module.css';
import { TaxIdentifierTypes } from '../../../../../../../enums/tax-identifiers/tax-identifiers-types';

const TAX_IDENTIFIER_TYPE_ID = 'taxIdentifierType';
const TAX_IDENTIFIER_NUMBER_ID = 'taxIdentifierNumber';

/** @typedef { import('./types').TaxIdentifierType}  TaxIdentifierType */
/**
 * @param {object} props
 * @param {TaxIdentifierType} props.taxIdentifier - selected tax identifier to be edited
 * @param {number} props.id - id of the element inside of the tax identifier table to be edited
 * @param {boolean} props.showClearButton - indicates if clear button needs to be displayed
 * @returns {JSX.Element} - The render EditRecipientTaxIdentifiersFormRow
 */
export const EditRecipientTaxIdentifiersFormRow = ({
  taxIdentifier = {
    redactedTaxNumber: '',
    type: '',
  },
  id = 0,
  showClearButton = true,
}) => {
  const intl = useIntl();
  const [touched, setTouched] = useState(false);
  const [backupTaxIdentifier, setBackupTaxIdentifier] = useState();

  const { updateTaxIdentifiers, validationErrors, saveDisabled, clearTaxIdentifier, removeTaxId, taxIdentifiers } =
    useEditRecipientTaxIdentifiersFormContext();

  const taxIdentifierOptionsFiltered = useMemo(
    () =>
      Object.keys(TaxIdentifierTypes)
        .filter((option) => !taxIdentifiers.some((taxId) => taxId.type === TaxIdentifierTypes[option].value))
        .sort(),
    [taxIdentifiers],
  );

  const taxNumberValidationMessage = touched && saveDisabled && validationErrors?.[id]?.taxNumber?.message;
  const typeValidationMessage = touched && saveDisabled && validationErrors?.[id]?.type?.message;
  const typeInlineMessageType = touched && typeValidationMessage ? INLINE_MESSAGE_TYPES.error : INLINE_MESSAGE_TYPES.sublabel;
  const taxNumberInlineMessageType = touched && taxNumberValidationMessage ? INLINE_MESSAGE_TYPES.error : INLINE_MESSAGE_TYPES.sublabel;

  const clear = useCallback(() => {
    clearTaxIdentifier();
  }, [clearTaxIdentifier]);

  const clearTaxIdentifierNumber = useCallback(() => {
    if (taxIdentifier?.redactedTaxNumber) {
      setBackupTaxIdentifier(taxIdentifier);
      updateTaxIdentifiers({ taxNumber: undefined }, id);
    }
  }, [id, taxIdentifier, updateTaxIdentifiers]);

  const updateTaxIdentifierNumber = useCallback(
    (event) => {
      const newValue = event?.target?.value;
      updateTaxIdentifiers({ taxNumber: newValue, isModified: true }, id);
      setTouched(true);
    },
    [id, updateTaxIdentifiers],
  );

  const updateTaxIdentifierType = (value) => {
    updateTaxIdentifiers({ type: value, isModified: true }, id);
    setTouched(true);
  };

  const handleOnBlur = useCallback(() => {
    if (!taxIdentifier.taxNumber && backupTaxIdentifier?.redactedTaxNumber) {
      const { redactedTaxNumber, type } = backupTaxIdentifier;
      updateTaxIdentifiers({ redactedTaxNumber, type }, id);
    } else {
      setBackupTaxIdentifier();
    }
  }, [taxIdentifier, backupTaxIdentifier, updateTaxIdentifiers, id]);

  return (
    <tr>
      <td>
        <DropdownMenu
          dropdownMenuShouldHaveWidthOfButton
          fullWidth
          id={TAX_IDENTIFIER_TYPE_ID}
          inlineMessage={typeValidationMessage}
          inlineMessageType={typeInlineMessageType}
          variant='form'
        >
          <DropdownMenuToggler>{taxIdentifier.type || <FormattedMessage {...messages.taxIdentifierTypeEmpty} />}</DropdownMenuToggler>
          {taxIdentifierOptionsFiltered.map((option) => {
            const { value, messageDescriptor } = TaxIdentifierTypes[option];

            return (
              <DropdownMenuItem key={value} onClick={() => updateTaxIdentifierType(value, id)} selected={taxIdentifier.type === value}>
                <FormattedMessage {...messageDescriptor} />
              </DropdownMenuItem>
            );
          })}
        </DropdownMenu>
      </td>
      <td>
        <Input
          className={style['tax-identifier-field']}
          id={TAX_IDENTIFIER_NUMBER_ID}
          inlineMessage={taxNumberValidationMessage}
          inlineMessageType={taxNumberInlineMessageType}
          name={TAX_IDENTIFIER_NUMBER_ID}
          onBlur={handleOnBlur}
          onChange={updateTaxIdentifierNumber}
          onClick={clearTaxIdentifierNumber}
          placeholder={intl.formatMessage(messages.taxIdentifierNumberPlaceholder)}
          value={taxIdentifier?.taxNumber || taxIdentifier?.redactedTaxNumber || ''}
          version={version2}
        />
      </td>
      <td>
        {showClearButton ? (
          <Button className={style['tax-id-button']} onClick={clear} theme='link'>
            <FormattedMessage {...messages.editRecipientTaxIdentifierClearButton} />
          </Button>
        ) : (
          <Button className={style['tax-id-button']} onClick={() => removeTaxId(id)} testId='remove-icon' theme='unstyled'>
            <FontAwesomeIcon className={style['remove-icon']} icon={faTrash} />
          </Button>
        )}
      </td>
    </tr>
  );
};
